import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { BaseComponent, BlueprintLocation, Building, PhotoLocation, PhotoModel } from '../types/protocols';
import { BaseComponentImpl } from '../types/implementations';
import { findBlueprintImage } from '../Helpers/blueprintLookup';
import { useFirebaseImage } from '../Helpers/ImageLoader';
import { LocationData, LocationOverlayShell } from './LocationDisplay';
import BlueprintWithMarkers from './BlueprintWithMarkers';
import PhotoWithMarker from './PhotoWithMarker';
import PhotoOverlay from './PhotoOverlay';
import './LocationViewer.css';

/**
 * Shows one of a component's positions (satellite image, drawing or reference
 * photo) with the same zoomable viewers as the component page: a map or drawing
 * in BlueprintWithMarkers, a photo in PhotoWithMarker. Both size themselves by
 * width, so the viewer measures its box and gives them the widest width whose
 * image still fits the height.
 */
export interface LocationViewerProps {
    location: LocationData;
    component: BaseComponent;
    building: Building;
    /**
     * Also cap the width so the image fits the box's height (default). Turn off
     * where the box has no height of its own (phones, where the page scrolls):
     * there the measured height is the viewer's own, and fitting to it would
     * shrink the viewer a pixel per layout pass.
     */
    fitHeight?: boolean;
}

/** What BlueprintWithMarkers needs to show one component's position on a map image or drawing. */
interface DrawingView {
    blueprint: PhotoModel;
    currentBlueprintKey: string;
    /** Drawn as the red map pin, like the component page, not as a round type icon. */
    pin: BlueprintLocation;
}

const drawingView = (location: LocationData, component: BaseComponent, building: Building): DrawingView | null => {
    const key = location.imageKey;
    if (!key) return null;
    if (location.type === 'map') {
        const image = building.model?.mapImages?.[key];
        const pin = component.mapLocations?.[key];
        if (!image || !pin) return null;
        return { blueprint: image, currentBlueprintKey: key, pin };
    }
    if (location.type === 'blueprint') {
        const found = findBlueprintImage(building.model?.structure, key);
        const pin = component.blueprintLocations?.[key];
        if (!found || !pin) return null;
        return { blueprint: found.image, currentBlueprintKey: key, pin };
    }
    return null;
};

const photoLocationOf = (location: LocationData): PhotoLocation | null =>
    location.type === 'photo' && location.parentKey
        ? {
            parentKey: location.parentKey,
            storagePath: location.storagePath,
            width: location.width,
            height: location.height,
            x: location.x,
            y: location.y,
        }
        : null;

/** Width/height of the shown image; the recorded reference frame has the same shape when the image's own size is unknown. */
const imageAspect = (location: LocationData, drawing: DrawingView | null): number => {
    const w = drawing?.blueprint.width || location.width;
    const h = drawing?.blueprint.height || location.height;
    return w > 0 && h > 0 ? w / h : 1;
};

/** The widest width at which an image of `aspect` fits the element's box (its width alone when `fitHeight` is off); 0 until measured. */
const useFitWidth = (ref: React.RefObject<HTMLDivElement | null>, aspect: number, fitHeight: boolean): number => {
    const [box, setBox] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        measure();
        return () => observer.disconnect();
    }, [ref]);
    return Math.floor(fitHeight ? Math.min(box.width, box.height * aspect) : box.width);
};

export const LocationViewer: React.FC<LocationViewerProps> = ({ location, component, building, fitHeight = true }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const drawing = useMemo(() => drawingView(location, component, building), [location, component, building]);
    const photo = photoLocationOf(location);
    const fitWidth = useFitWidth(containerRef, imageAspect(location, drawing), fitHeight);

    if (!drawing && !photo) {
        return <div className="location-viewer location-viewer-error">Kunde inte visa positionen</div>;
    }

    return (
        <div ref={containerRef} className="location-viewer">
            {fitWidth > 0 && (
                <div className="location-viewer-fit" style={{ width: `${fitWidth}px` }}>
                    {drawing ? (
                        <BlueprintWithMarkers
                            blueprint={drawing.blueprint}
                            currentBlueprintKey={drawing.currentBlueprintKey}
                            pointMarker={drawing.pin}
                            pointMarkerLabel={new BaseComponentImpl(component).displayName(building)}
                            building={building}
                            hideTitle
                        />
                    ) : photo && (
                        <PhotoWithMarker photoLocation={photo} hideTitle />
                    )}
                </div>
            )}
        </div>
    );
};

/** A reference photo full screen: PhotoOverlay once the photo's URL is known, a spinner in the shell until then. */
const PhotoLocationOverlay: React.FC<{ photo: PhotoLocation; label: string; onClose: () => void }> = ({ photo, label, onClose }) => {
    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);
    if (!isLoading && src && !hasError) {
        return <PhotoOverlay src={src} photoLocation={photo} onClose={onClose} />;
    }
    return (
        <LocationOverlayShell label={label} onClose={onClose}>
            {hasError
                ? <div className="location-display-modal-error">Kunde inte ladda bild</div>
                : <Loader2 size={32} className="location-display-spinner" />}
        </LocationOverlayShell>
    );
};

/**
 * The same position full screen. A photo opens the component page's
 * PhotoOverlay; a map or drawing gets the zoomable viewer in the overlay shell.
 */
export const LocationOverlay: React.FC<LocationViewerProps & { onClose: () => void }> = ({ location, component, building, onClose }) => {
    const photo = photoLocationOf(location);
    if (photo) {
        return <PhotoLocationOverlay photo={photo} label={location.label} onClose={onClose} />;
    }
    return (
        <LocationOverlayShell label={location.label} onClose={onClose}>
            {/* A click on the viewer is a zoom or pan, not a close */}
            <div className="location-viewer-overlay-body" onClick={(e) => e.stopPropagation()}>
                <LocationViewer location={location} component={component} building={building} />
            </div>
        </LocationOverlayShell>
    );
};
